import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Boxes, Check, Mail, Plus } from 'lucide-react';

import {
  getMailboxRoutes,
  getWorkflowsCollection,
  MailboxRoute,
} from '$api/WorkflowsAPI';
import { AdminShell } from '$layouts/v2/AdminShell';
import { CreateWorkflowModal } from '$layouts/v2/components/CreateWorkflowModal';
import { paths } from '$routes/paths';
import { useAccessToken } from '$src/authConfig';

type WorkflowTab = 'email' | 'reusable';

function WorkflowStatus({ workflow }: { workflow: v4.WorkflowHeadSpec }) {
  return workflow.activeVersion != null ? (
    <Chip
      size="small"
      color="success"
      icon={<Check size={11} />}
      label={`Live v${workflow.activeVersion}`}
    />
  ) : (
    <Chip size="small" variant="outlined" label="Not live" />
  );
}

function EmptyState({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Stack
      spacing={1}
      sx={{
        alignItems: 'center',
        py: 5,
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      {icon}
      <Typography
        sx={{
          color: 'text.primary',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ maxWidth: 440 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<v4.WorkflowHeadSpec[] | null>(
    null,
  );
  const [routes, setRoutes] = useState<MailboxRoute[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [routesError, setRoutesError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<WorkflowTab>('email');
  const { token } = useAccessToken();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    setRoutesError(null);
    try {
      const [workflowData, routeResult] = await Promise.all([
        getWorkflowsCollection(token),
        getMailboxRoutes(token).then(
          (data) => ({ data, error: null }),
          (error_) => ({ data: null, error: String(error_) }),
        ),
      ]);
      setWorkflows(workflowData);
      setRoutes(routeResult.data);
      setRoutesError(routeResult.error);
    } catch (error_) {
      setError(String(error_));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const emailWorkflows = useMemo(
    () =>
      workflows?.filter((workflow) => workflow.trigger !== 'workflow') ?? [],
    [workflows],
  );
  const reusableWorkflows = useMemo(
    () =>
      workflows?.filter((workflow) => workflow.trigger === 'workflow') ?? [],
    [workflows],
  );
  const workflowsById = useMemo(
    () =>
      new Map(workflows?.map((workflow) => [workflow.workflowId, workflow])),
    [workflows],
  );
  const routesByWorkflow = useMemo(() => {
    const grouped = new Map<string, MailboxRoute[]>();
    routes?.forEach((route) =>
      grouped.set(route.workflowId, [
        ...(grouped.get(route.workflowId) ?? []),
        route,
      ]),
    );
    return grouped;
  }, [routes]);
  const unassignedEmailWorkflows = emailWorkflows.filter(
    (workflow) => !routesByWorkflow.get(workflow.workflowId)?.length,
  );
  const connectedWorkflowGroups = useMemo(
    () =>
      Array.from(routesByWorkflow.entries())
        .map(([workflowId, mailboxRoutes]) => ({
          workflowId,
          workflow: workflowsById.get(workflowId),
          routes: mailboxRoutes.toSorted((a, b) =>
            a.mailbox.localeCompare(b.mailbox),
          ),
        }))
        .toSorted((a, b) =>
          (a.workflow?.title ?? a.workflowId).localeCompare(
            b.workflow?.title ?? b.workflowId,
          ),
        ),
    [routesByWorkflow, workflowsById],
  );

  return (
    <AdminShell
      title="Workflows"
      actions={
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setCreating(true)}
        >
          New workflow
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, nextTab: WorkflowTab) => setActiveTab(nextTab)}
        aria-label="Workflow categories"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="email"
          icon={<Mail size={16} />}
          iconPosition="start"
          label={`Email triggered workflows (${emailWorkflows.length})`}
        />
        <Tab
          value="reusable"
          icon={<Boxes size={16} />}
          iconPosition="start"
          label={`Reusable workflows (${reusableWorkflows.length})`}
        />
      </Tabs>

      {activeTab === 'email' && (
        <Card sx={{ mb: 3 }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1.5,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.5,
                bgcolor: 'action.selected',
                color: 'secondary.main',
              }}
            >
              <Mail size={18} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>
                Email triggered workflows
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Workflows started automatically when a new email arrives.
                Connect mailboxes to decide which workflow handles each inbox.
              </Typography>
            </Box>
            <Button
              startIcon={<Mail size={15} />}
              onClick={() =>
                navigate(paths.dashboard.mailAutomation.settings.root)
              }
            >
              Manage mailboxes
            </Button>
          </Box>
          <Divider />
          {routesError ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              Mailbox routes could not be loaded. {routesError}
            </Alert>
          ) : routes === null || workflows === null ? (
            <Typography
              sx={{
                color: 'text.secondary',
                p: 3,
              }}
            >
              Loading mailbox routes…
            </Typography>
          ) : routes.length === 0 ? (
            <EmptyState
              icon={<Mail size={28} />}
              title="No mailboxes are connected yet"
            >
              Add a mailbox route to decide which workflow runs for incoming
              email.
            </EmptyState>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {connectedWorkflowGroups.map(
                ({ workflowId, workflow, routes: mailboxRoutes }) => {
                  return (
                    <CardActionArea
                      key={workflowId}
                      onClick={() =>
                        workflow &&
                        navigate(
                          `/v2/mail-automation/workflows/${encodeURIComponent(
                            workflow.workflowId,
                          )}`,
                        )
                      }
                      sx={{ px: 2, py: 1.5 }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        sx={{
                          alignItems: { xs: 'flex-start', sm: 'center' },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: 'center',
                            minWidth: { sm: 260 },
                          }}
                        >
                          <Mail size={17} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 600 }}>
                              {workflow?.title ?? workflowId}
                            </Typography>
                            {workflow && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                {workflow.workflowId}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Stack
                          direction="row"
                          sx={{
                            flexWrap: 'wrap',
                            gap: 0.75,
                            flexGrow: 1,
                          }}
                        >
                          {mailboxRoutes.map((route) => (
                            <Chip
                              key={route.id}
                              size="small"
                              icon={<Mail size={11} />}
                              label={`${route.mailbox}${
                                route.enabled ? '' : ' (disabled)'
                              }`}
                              variant={route.enabled ? 'filled' : 'outlined'}
                            />
                          ))}
                        </Stack>
                        {workflow && <WorkflowStatus workflow={workflow} />}
                      </Stack>
                    </CardActionArea>
                  );
                },
              )}
            </Stack>
          )}
          {routes !== null && unassignedEmailWorkflows.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Email workflows not connected to a mailbox
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {unassignedEmailWorkflows.map((workflow) => (
                    <Chip
                      key={workflow.id}
                      icon={<Mail size={13} />}
                      label={workflow.title}
                      onClick={() =>
                        navigate(
                          `/v2/mail-automation/workflows/${encodeURIComponent(
                            workflow.workflowId,
                          )}`,
                        )
                      }
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Card>
      )}

      {activeTab === 'reusable' && (
        <Card>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                color: 'text.secondary',
              }}
            >
              <Boxes size={18} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Reusable workflows
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Building blocks called by other workflows. They do not receive
                mail directly.
              </Typography>
            </Box>
          </Box>
          <Divider />
          {workflows === null ? (
            <Typography
              sx={{
                color: 'text.secondary',
                p: 3,
              }}
            >
              Loading…
            </Typography>
          ) : reusableWorkflows.length === 0 ? (
            <EmptyState
              icon={<Boxes size={28} />}
              title="No reusable workflows yet"
            >
              Create one when you need to share a sequence of steps between
              workflows.
            </EmptyState>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 1.5,
                p: 1.5,
              }}
            >
              {reusableWorkflows.map((workflow) => (
                <Card key={workflow.id} variant="outlined">
                  <CardActionArea
                    onClick={() =>
                      navigate(
                        `/v2/mail-automation/workflows/${encodeURIComponent(
                          workflow.workflowId,
                        )}`,
                      )
                    }
                    sx={{ p: 1.5 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <Boxes size={16} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 600 }}>
                          {workflow.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {workflow.workflowId}
                        </Typography>
                      </Box>
                      <WorkflowStatus workflow={workflow} />
                    </Stack>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          )}
        </Card>
      )}

      {creating && (
        <CreateWorkflowModal
          defaultTriggerId="email"
          onCancel={() => setCreating(false)}
          onCreated={(workflowId) => {
            setCreating(false);
            navigate(
              `/v2/mail-automation/workflows/${encodeURIComponent(workflowId)}`,
            );
          }}
        />
      )}
    </AdminShell>
  );
}
